import React, { useMemo, memo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, Trophy, Zap, Target, Calendar, Award, Flame } from 'lucide-react';
import { BADGES } from '../../gamification/hooks/useGameification';
import { Z_CLASS } from '../../../constants/zIndex';
import { cn } from '../../../shared/utils/cn';
import { haptic } from '../../../shared/utils/haptics';

/**
 * StatsPanel - Complete statistics panel
 * Displays XP, level, streaks, badges, and weekly stats
 */
export const StatsPanel = memo(function StatsPanel({
  totalXP,
  todayXP,
  level,
  levelProgress,
  currentStreak,
  longestStreak,
  tasksCompleted,
  vitalTasksCompleted,
  unlockedBadges,
  weeklyStats,
  focusTime,
  proofsCount,
  onClose
}) {
  const { unlocked, locked } = useMemo(() => {
    const all = Object.values(BADGES);
    return {
      unlocked: all.filter(b => unlockedBadges.includes(b.id)),
      locked: all.filter(b => !unlockedBadges.includes(b.id)).slice(0, 4)
    };
  }, [unlockedBadges]);

  const handleClose = useCallback(() => {
    haptic.tap();
    onClose();
  }, [onClose]);

  return (
    <motion.div
      className={cn(
        'fixed inset-0 flex items-center justify-center p-2',
        'glass-overlay',
        Z_CLASS.STATS_PANEL
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="glass-elevated w-full max-w-md max-h-[95svh] overflow-y-auto flex flex-col"
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
      >
        {/* Header */}
        <div className="bg-[var(--bg-elevated,#111114)] p-4 flex-shrink-0 border-b border-white/[0.06]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 glass-subtle rounded-lg flex items-center justify-center">
                <Trophy className="w-4 h-4 text-zinc-500" />
              </div>
              <h2 className="font-medium text-sm text-zinc-100">Statistiques</h2>
            </div>
            <motion.button
              onClick={handleClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <X className="w-4 h-4 text-zinc-400" />
            </motion.button>
          </div>

          {/* Level and XP */}
          <div className="flex items-center gap-4">
            <motion.div
              className="flex flex-col items-center justify-center w-14 h-14 glass-card"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <span className="text-lg font-semibold text-zinc-100">{level}</span>
              <span className="text-[9px] text-zinc-500 uppercase tracking-wide">Niveau</span>
            </motion.div>
            <div className="flex-grow">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-zinc-300 font-medium">{totalXP} XP</span>
                <span className="text-zinc-600 text-[10px]">Prochain niveau</span>
              </div>
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-zinc-600 to-zinc-500 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${levelProgress}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-grow overflow-y-auto p-4 space-y-4 bg-[var(--bg-base,#09090b)]">
          {/* Quick stats */}
          <div className="grid grid-cols-2 gap-2">
            <StatCard icon={Zap} label="XP aujourd'hui" value={`+${todayXP}`} />
            <StatCard
              icon={Flame}
              label="Série actuelle"
              value={`${currentStreak}j`}
              subtext={`Record: ${longestStreak}j`}
            />
            <StatCard icon={Target} label="Tâches complétées" value={tasksCompleted} />
            <StatCard icon={Award} label="Vitaux complétés" value={vitalTasksCompleted} />
          </div>

          {/* Weekly stats */}
          {weeklyStats && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-zinc-500" />
                <h3 className="text-xs font-medium text-zinc-400">Cette semaine</h3>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <MiniStat value={weeklyStats.tasksCompleted || 0} label="Tâches" />
                <MiniStat value={weeklyStats.xpEarned || 0} label="XP" />
                <MiniStat value={weeklyStats.vitalsCompleted || 0} label="Vitaux" />
              </div>
            </motion.div>
          )}

          {/* Unlocked badges */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-zinc-500" />
              <h3 className="text-xs font-medium text-zinc-400">Badges ({unlocked.length})</h3>
            </div>
            {unlocked.length > 0 ? (
              <div className="grid grid-cols-4 gap-2">
                {unlocked.map((badge, i) => (
                  <motion.div
                    key={badge.id}
                    className="flex flex-col items-center gap-1 p-2 glass-card"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.05 }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <span className="text-xl">{badge.icon}</span>
                    <span className="text-[9px] text-zinc-500 text-center truncate w-full">
                      {badge.name}
                    </span>
                  </motion.div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-600 text-center py-2">
                Complète des tâches pour débloquer des badges !
              </p>
            )}
          </motion.div>

          {/* Locked badges preview */}
          {locked.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h3 className="text-xs font-medium text-zinc-500 mb-2">Prochains badges</h3>
              <div className="space-y-2">
                {locked.map((badge, i) => (
                  <motion.div
                    key={badge.id}
                    className="flex items-center gap-3 p-2 bg-zinc-900/50 border border-zinc-800/50 rounded-lg opacity-60"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 0.6, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <span className="text-lg">🔒</span>
                    <div className="flex-1 min-w-0">
                      <span className="block text-xs font-medium text-zinc-400">{badge.name}</span>
                      <span className="block text-[10px] text-zinc-600 truncate">
                        {badge.description}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Focus time and proofs */}
          <div className="grid grid-cols-2 gap-2">
            {focusTime !== undefined && (
              <motion.div
                className="flex items-center gap-3 p-3 glass-card"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
              >
                <Target className="w-5 h-5 text-zinc-500" />
                <div>
                  <span className="block text-sm font-medium text-zinc-200">{focusTime} min</span>
                  <span className="block text-[10px] text-zinc-500">Temps focus</span>
                </div>
              </motion.div>
            )}
            {proofsCount !== undefined && (
              <motion.div
                className="flex items-center gap-3 p-3 glass-card"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Trophy className="w-5 h-5 text-zinc-500" />
                <div>
                  <span className="block text-sm font-medium text-zinc-200">{proofsCount}</span>
                  <span className="block text-[10px] text-zinc-500">Preuves</span>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 bg-[var(--bg-elevated,#111114)] border-t border-white/[0.06] flex-shrink-0">
          <p className="text-center text-xs text-zinc-500">
            {currentStreak > 0
              ? `${currentStreak} jours de suite`
              : "Commence ta série aujourd'hui"}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
});

// Stat card component
const StatCard = memo(function StatCard({ icon: Icon, label, value, subtext }) {
  return (
    <motion.div
      className="glass-card p-3"
      whileHover={{ scale: 1.02 }}
    >
      <Icon className="w-3.5 h-3.5 text-zinc-600 mb-2" />
      <div className="space-y-0.5">
        <span className="block text-base font-medium text-zinc-200">{value}</span>
        <span className="block text-[10px] text-zinc-500">{label}</span>
        {subtext && <span className="block text-[9px] text-zinc-600">{subtext}</span>}
      </div>
    </motion.div>
  );
});

// Mini stat component
const MiniStat = memo(function MiniStat({ value, label }) {
  return (
    <div className="glass-subtle p-2 text-center">
      <span className="block text-sm font-medium text-zinc-200">{value}</span>
      <span className="block text-[10px] text-zinc-500">{label}</span>
    </div>
  );
});

/**
 * StreakCalendar - Compact streak visualization
 */
export const StreakCalendar = memo(function StreakCalendar({ entries, currentStreak }) {
  const days = useMemo(() => {
    const result = [];
    const today = new Date();

    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const entry = entries?.find(e => e.date === dateStr);

      result.push({
        date: dateStr,
        day: date.getDate(),
        completed: !!entry,
        isToday: i === 0
      });
    }

    return result;
  }, [entries]);

  return (
    <div className="p-4 glass-card">
      <div className="flex items-center gap-2 mb-3">
        <Flame className="w-4 h-4 text-amber-500" />
        <span className="text-xs font-medium text-zinc-300">Série : {currentStreak} jours</span>
      </div>
      <div className="grid grid-cols-10 gap-1">
        {days.map((day, i) => (
          <motion.div
            key={i}
            className={cn(
              'w-full aspect-square rounded-sm transition-colors',
              day.completed ? 'bg-amber-500/60' : 'bg-zinc-800',
              day.isToday && 'ring-1 ring-zinc-500'
            )}
            title={day.date}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.01 }}
          />
        ))}
      </div>
    </div>
  );
});

export default StatsPanel;
